import { Provider, IAgentRuntime, Memory, State, elizaLogger, ProviderResult } from '@elizaos/core';
import { ZapperConfig } from '../types';
import {
  AccountInfoData,
  AccountInfoResponse,
  AccountResolutionResult,
  FarcasterProfileResponse,
  FarcasterProfileData,
} from '../types/account';
import { fetchWithPolyfill } from '../utils/fetchWithPolyfill';

export const accountProvider: Provider = {
  name: 'accountProvider',
  description:
    'Provides social profile data, ENS resolution, and Farcaster account information for wallet addresses',
  dynamic: true,
  get: async (runtime: IAgentRuntime, message: Memory, state: State): Promise<ProviderResult> => {
    elizaLogger.debug('Account provider called');

    const hasApiKey = runtime.getSetting('ZAPPER_API_KEY');
    if (!hasApiKey) {
      elizaLogger.error('ZAPPER_API_KEY not configured');
      return {
        text: 'Account provider requires ZAPPER_API_KEY configuration',
        data: { error: 'API key not configured' },
      };
    }

    const zapperConfig: ZapperConfig = {
      apiKey: runtime.getSetting('ZAPPER_API_KEY'),
      apiUrl: runtime.getSetting('ZAPPER_API_URL') || 'https://public.zapper.xyz/graphql',
    };

    // Extract wallet address from message
    const messageText = message.content.text || '';
    const walletAddress = extractWalletAddress(messageText);

    if (!walletAddress) {
      elizaLogger.debug(
        'accountProvider: No wallet address found in message, returning neutral context'
      );
      return {
        text: 'Account data context available when wallet address is provided',
        data: { provider: 'accountProvider', status: 'neutral' },
      };
    }

    try {
      elizaLogger.debug(`Fetching account data for address: ${walletAddress}`);

      const accountData = await fetchAccountData(zapperConfig, walletAddress);

      if (!accountData) {
        return {
          text: `Account data not available for address ${walletAddress}`,
          data: { provider: 'accountProvider', status: 'no-data', address: walletAddress },
        };
      }

      const formattedResult = formatAccountResult(accountData);

      elizaLogger.debug('Account data fetched successfully');
      return {
        text: formattedResult,
        data: { address: walletAddress, accountData },
        values: {
          address: walletAddress,
          hasENS: accountData.ens ? true : false,
          hasFarcaster: accountData.farcasterUsername ? true : false,
          followerCount: accountData.followStats?.followerCount || 0,
          followingCount: accountData.followStats?.followingCount || 0,
        },
      };
    } catch (error) {
      elizaLogger.error('accountProvider: Error fetching account data:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        address: walletAddress,
        apiUrl: zapperConfig.apiUrl,
        hasApiKey: !!zapperConfig.apiKey,
      });
      return {
        text: 'Account data temporarily unavailable',
        data: {
          provider: 'accountProvider',
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error',
          address: walletAddress,
        },
      };
    }
  },
};

function extractWalletAddress(text: string): string | null {
  // Look for Ethereum address pattern (0x followed by 40 hex chars)
  const addressPattern = /0x[a-fA-F0-9]{40}/;
  const addressMatch = text.match(addressPattern);

  if (addressMatch) {
    return addressMatch[0];
  }

  // Look for ENS names
  const ensPattern = /\b\w+\.eth\b/;
  const ensMatch = text.match(ensPattern);

  if (ensMatch) {
    return ensMatch[0];
  }

  // Look for basename
  const basenamePattern = /\b\w+\.base\b/;
  const basenameMatch = text.match(basenamePattern);

  if (basenameMatch) {
    return basenameMatch[0];
  }

  return null;
}

async function fetchAccountData(
  config: ZapperConfig,
  identifier: string
): Promise<AccountResolutionResult | null> {
  try {
    const query = `
      query GetAccountData($addresses: [Address!], $ens: [String!], $basenames: [String!]) {
        accounts(
          addresses: $addresses
          ens: $ens
          basenames: $basenames
        ) {
          address
          displayName {
            value
            source
          }
          avatar {
            value {
              ... on AvatarUrl {
                url
              }
              ... on NftToken {
                tokenId
              }
            }
            source
          }
          description {
            value
            source
          }
          socialLinks {
            url
            name
            source
          }
          ensRecord {
            name
            metadata {
              description
              avatar {
                ... on AvatarUrl {
                  url
                }
                ... on NftToken {
                  tokenId
                }
              }
              github
              twitter
              email
            }
          }
          basename
          lensProfile {
            handle
          }
          farcasterProfile {
            username
            fid
            custodyAddress
            connectedAddresses
            metadata {
              displayName
              description
              imageUrl
              warpcast
              xAccount {
                username
                url
              }
            }
            followStats {
              followerCount
              followingCount
            }
          }
          followStats {
            followerCount
            followingCount
          }
          isContract
          contract {
            address
            network
          }
        }
      }
    `;

    const variables: any = {};

    // Check if identifier is an address
    if (identifier.startsWith('0x')) {
      variables.addresses = [identifier];
    } else if (identifier.endsWith('.eth')) {
      variables.ens = [identifier];
    } else if (identifier.endsWith('.base')) {
      variables.basenames = [identifier];
    }

    const response = await fetchWithPolyfill(config.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-zapper-api-key': config.apiKey,
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      elizaLogger.error('accountProvider: HTTP error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
        url: config.apiUrl,
        identifier: identifier,
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result = (await response.json()) as AccountInfoResponse;

    if (result.data?.accounts && result.data.accounts.length > 0) {
      return mapAccountDataToResult(result.data.accounts[0]);
    }

    return null;
  } catch (error) {
    elizaLogger.error('accountProvider: Failed to fetch account data:', {
      error: error instanceof Error ? error.message : String(error),
      identifier: identifier,
      apiUrl: config.apiUrl,
    });
    return null;
  }
}

function mapAccountDataToResult(account: AccountInfoData): AccountResolutionResult {
  let avatar: string | undefined;

  if (account.avatar && typeof account.avatar.value === 'object' && 'url' in account.avatar.value) {
    avatar = (account.avatar.value as any).url;
  } else if (
    account.avatar &&
    typeof account.avatar.value === 'object' &&
    'tokenId' in account.avatar.value
  ) {
    // Handle NFT token avatar - for now just use a placeholder
    avatar = `https://api.zapper.xyz/nft/image/${(account.avatar.value as any).tokenId}`;
  }

  return {
    address: account.address,
    ens: account.ensRecord?.name,
    basename: account.basename,
    farcasterUsername: account.farcasterProfile?.username,
    fid: account.farcasterProfile?.fid,
    displayName: account.displayName.value,
    avatar,
    description: account.description?.value,
    socialLinks: account.socialLinks || [],
    followStats: account.followStats || account.farcasterProfile?.followStats,
    isContract: account.isContract,
    badges: account.badges,
  };
}

function formatAccountResult(account: AccountResolutionResult): string {
  const parts: string[] = [];

  // Basic account info
  parts.push(`Account Information for ${account.displayName}:`);
  parts.push(`- Address: ${account.address}`);

  if (account.ens) {
    parts.push(`- ENS: ${account.ens}`);
  }

  if (account.basename) {
    parts.push(`- Basename: ${account.basename}`);
  }

  if (account.farcasterUsername) {
    parts.push(`- Farcaster: @${account.farcasterUsername} (FID: ${account.fid})`);
  }

  if (account.description) {
    parts.push(`- Description: ${account.description}`);
  }

  // Social stats
  if (account.followStats) {
    parts.push(
      `- Social Stats: ${account.followStats.followerCount} followers, ${account.followStats.followingCount} following`
    );
  }

  // Social links
  if (account.socialLinks && account.socialLinks.length > 0) {
    parts.push(
      `- Social Links: ${account.socialLinks.map((link) => `${link.name}: ${link.url}`).join(', ')}`
    );
  }

  // Account type
  parts.push(
    `- Account Type: ${account.isContract ? 'Contract' : 'EOA (Externally Owned Account)'}`
  );

  // Badges
  if (account.badges && account.badges.length > 0) {
    parts.push(`- Badges: ${account.badges.map((badge) => badge.badgeName).join(', ')}`);
  }

  return parts.join('\n');
}
